'use client';

import {
  PortalRealtimeClientEvent,
  PUBLIC_REALTIME_NAMESPACE,
  type RealtimeMessage,
  RealtimeServiceEventType,
} from '@govinity/shared-config';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/env.mjs';
import { clientInstanceId } from '@/lib/client-instance-id';
import { useGovernmentStore } from '@/stores/governmentStore';

type RealtimeProviderProps = {
  children: React.ReactNode;
  governmentCode: string;
  governmentId: number;
};

/**
 * Connects to the realtime service for the current portal government, subscribes to that
 * government's broadcast room after connect, and disconnects when the layout unmounts.
 * Invalidates public meeting queries when `MEETING_EVENT_CHANGE` messages arrive — including the
 * one emitted when a legislative item's security level changes, so the agenda re-fetches its
 * title-only-vs-full state without a manual refresh (GOV-1476).
 */
export function RealtimeProvider({
  children,
  governmentCode,
  governmentId,
}: RealtimeProviderProps) {
  const queryClient = useQueryClient();
  const invalidateMeetingsCache = useGovernmentStore((state) => state.invalidateMeetingsCache);

  useEffect(() => {
    const realtimeServiceUrl = env.NEXT_PUBLIC_REALTIME_SERVICE_URL;

    if (!realtimeServiceUrl) {
      return;
    }

    const socket: Socket = io(`${realtimeServiceUrl}${PUBLIC_REALTIME_NAMESPACE}`, {
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 5000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
    });

    socket.on('connect', () => {
      socket.emit(PortalRealtimeClientEvent.SUBSCRIBE_GOVERNMENT, { governmentId });
    });

    socket.on('message', (data: RealtimeMessage) => {
      if (data.payload.originatorClientId === clientInstanceId) {
        return;
      }

      if (data.payload.governmentId !== governmentId) {
        return;
      }

      const code = governmentCode;

      switch (data.type) {
        case RealtimeServiceEventType.PUBLIC_MEETING_EVENT_CHANGE: {
          const { meetingEventId, itemSecurityChange } = data.payload;

          queryClient.invalidateQueries({ queryKey: ['meeting', code, meetingEventId] });
          queryClient.invalidateQueries({
            queryKey: ['meeting-live-stream', code, meetingEventId],
          });
          queryClient.invalidateQueries({ queryKey: ['meeting-recording', code, meetingEventId] });
          queryClient.invalidateQueries({ queryKey: ['meeting-timestamps', code, meetingEventId] });
          // Advances the live "Now:" indicator on the meeting page each time an entry is floored —
          // flooring emits this government-room event (GOV-1761).
          queryClient.invalidateQueries({
            queryKey: ['meeting-current-live-item', code, meetingEventId],
          });
          queryClient.invalidateQueries({ queryKey: ['meetings-calendar', code] });
          queryClient.invalidateQueries({ queryKey: ['meetings-list', code] });
          queryClient.invalidateQueries({ queryKey: ['meetings-list-opposite-count', code] });
          queryClient.invalidateQueries({ queryKey: ['meetings-years', code] });
          queryClient.invalidateQueries({ queryKey: ['liveMeetings', code] });
          queryClient.invalidateQueries({ queryKey: ['upcomingMeetings', code] });
          queryClient.invalidateQueries({ queryKey: ['meetings-transcript-search', code] });

          // This event fires often (live console, speaker timer, etc.), so only a genuine security
          // flip — which can 404 a topic page or flip an item's detail across search/document
          // surfaces — invalidates those heavier caches (GOV-1476).
          if (itemSecurityChange) {
            queryClient.invalidateQueries({ queryKey: ['topic'] });
            queryClient.invalidateQueries({ queryKey: ['legislative-items-search', code] });
            queryClient.invalidateQueries({ queryKey: ['full-search', code] });
            queryClient.invalidateQueries({ queryKey: ['public-documents', code] });
            queryClient.invalidateQueries({ queryKey: ['public-documents-full-search', code] });
            queryClient.invalidateQueries({ queryKey: ['public-documents-filter-options', code] });
            queryClient.invalidateQueries({
              queryKey: ['public-documents-filter-options-years', code],
            });
          }

          invalidateMeetingsCache();
          break;
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [governmentCode, governmentId, queryClient, invalidateMeetingsCache]);

  return <>{children}</>;
}
